import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  bulkSetWellboreExclusionsAtom,
  bundleOfSticksDetailsAtom,
  createdSubtractDetailsAtom,
  createdSubtractGeometryAtom,
  createdTractDetailsAtom,
  createdTractGeometryAtom,
  currentDevelopmentTractIntersectionAnalysisLoadableAtom,
  futureDevelopmentTractIntersectionAnalysisLoadableAtom,
  nriReviewDataLoadableAtom,
  refetchNriDataAtom,
  retriggerTractIntersectionAnalysisAtom,
  stagedExcludedUwisAtom,
  tiaWorkflowStatusAtom,
  upsertCrmTractNotificationsLoadableAtom,
  wellboreExclusionsLoadableAtom,
  workflowStatusAtom,
} from './atoms';
import {
  mapSlotNRIResponseToTractNRI,
  mapSlotNRIResponseToSlotNRI,
} from 'utils/misc';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { subscribe } from 'lib/pusher';
import { StatusItem, WorkflowStepData } from './types';

export const useTractIntersectionRequestAnalysis = () => {
  const [tiaWorkflowStatus, setTiaWorkflowStatus] = useAtom(
    tiaWorkflowStatusAtom
  );

  const currentDevelopmentLoadable = useAtomValue(
    currentDevelopmentTractIntersectionAnalysisLoadableAtom
  );

  const futureDevelopmentLoadable = useAtomValue(
    futureDevelopmentTractIntersectionAnalysisLoadableAtom
  );

  const retriggerTractIntersectionAnalysis = useSetAtom(
    retriggerTractIntersectionAnalysisAtom
  );

  const refetchNriData = useSetAtom(refetchNriDataAtom);

  // Subscribe to pusher for TIA progress updates
  useEffect(
    () => {
      let subscription: any;
      if (tiaWorkflowStatus?.status === 'READY') {
        subscription = subscribe(tiaWorkflowStatus.channel);
        subscription.bind('analysis_progress', (data: any) => {
          if (data.status === 'processed') {
            setTiaWorkflowStatus({
              ...tiaWorkflowStatus,
              status: 'PROCESSED',
            });
            // Refetch NRI data (which includes slot allocations) when TIA completes
            refetchNriData();
          }
        });
      }
      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tiaWorkflowStatus?.status, setTiaWorkflowStatus, refetchNriData]
  );

  const currentDevelopmentTractIntersectionAnalysis =
    currentDevelopmentLoadable.state === 'hasData'
      ? currentDevelopmentLoadable.data
      : undefined;

  const futureDevelopmentTractIntersectionAnalysis =
    futureDevelopmentLoadable.state === 'hasData'
      ? futureDevelopmentLoadable.data
      : undefined;

  const isLoading =
    tiaWorkflowStatus === undefined ||
    tiaWorkflowStatus.status === 'READY' ||
    tiaWorkflowStatus.status === 'IDLE' ||
    currentDevelopmentLoadable.state === 'loading' ||
    futureDevelopmentLoadable.state === 'loading';

  return {
    tiaWorkflowStatus,
    currentDevelopmentTractIntersectionAnalysis,
    futureDevelopmentTractIntersectionAnalysis,
    retriggerTractIntersectionAnalysis,
    isLoading,
  };
};

export const useTractWorkflow = () => {
  const fetchTractDetails = useSetAtom(createdTractDetailsAtom);
  const fetchSubtractDetails = useSetAtom(createdSubtractDetailsAtom);
  const fetchTractGeometry = useSetAtom(createdTractGeometryAtom);
  const fetchSubtractGeometry = useSetAtom(createdSubtractGeometryAtom);
  const fetchBundleOfSticksDetails = useSetAtom(bundleOfSticksDetailsAtom);
  const [workflowStatus, dispatch] = useAtom(workflowStatusAtom);
  const setTiaWorkflowStatus = useSetAtom(tiaWorkflowStatusAtom);
  const refetchNriData = useSetAtom(refetchNriDataAtom);
  const notificationsLoadable = useAtomValue(
    upsertCrmTractNotificationsLoadableAtom
  );
  const notifications =
    notificationsLoadable.state === 'hasData'
      ? notificationsLoadable.data
      : undefined;

  const channel = notifications?.channel;

  const initialStatusItems = useMemo<StatusItem[]>(
    () =>
      (notifications?.events || [])
        .filter((event: { step_type?: string }) => event.step_type)
        .map((event) => ({
          label: `${event.label}:`,
          value: 'Pending',
          name: event.name,
        })),
    [notifications?.events]
  );

  const processedHandlers = useMemo<
    Record<string, (data: WorkflowStepData) => void>
  >(
    () => ({
      'crm-tract-salesforce-update-status-changed': (data) =>
        data.href && fetchTractDetails(data.href),
      'crm-tract-salesforce-create-status-changed': (data) =>
        data.href && fetchTractDetails(data.href),
      'crm-subtract-salesforce-fetch-status-changed': (data) =>
        data.href && fetchSubtractDetails(data.href),
      'crm-subtract-salesforce-sync-status-changed': (data) =>
        data.href && fetchSubtractDetails(data.href),
      'crm-subtract-geometry-create-status-changed': (data) =>
        data.href && fetchSubtractGeometry(data.href),
      'crm-subtract-geometry-update-status-changed': (data) =>
        data.href && fetchSubtractGeometry(data.href),
      'crm-tract-geometry-create-status-changed': (data) =>
        data.href && fetchTractGeometry(data.href),
      'crm-tract-geometry-update-status-changed': (data) =>
        data.href && fetchTractGeometry(data.href),
      'crm-tract-bundle-of-sticks-status-changed': (data) =>
        data.href && fetchBundleOfSticksDetails(data.href),
      'crm-tract-intersection-analysis-status-changed': (data) => {
        setTiaWorkflowStatus({
          id: Number(data.output?.tia_id ?? 0),
          channel: data.output?.channel_name ?? '',
          status: 'PROCESSED',
        });
        // NRI calculations are generated when TIA completes, so refetch NRI data
        refetchNriData();
      },
    }),
    [
      fetchTractDetails,
      fetchSubtractDetails,
      fetchSubtractGeometry,
      fetchTractGeometry,
      fetchBundleOfSticksDetails,
      setTiaWorkflowStatus,
      refetchNriData,
    ]
  );

  // Track the last initialized status items to detect when a new workflow starts
  const lastInitializedItemsRef = useRef<StatusItem[]>([]);

  // Initialize status items when:
  // 1. statusItems is empty (after reset or first load), OR
  // 2. initialStatusItems changed (new workflow created) - this handles the race
  //    condition where reset clears items but old initialStatusItems causes
  //    premature initialization before new API response arrives
  useEffect(() => {
    if (initialStatusItems.length === 0) {
      return;
    }

    // Check if initialStatusItems reference changed (new workflow)
    const isNewWorkflow =
      lastInitializedItemsRef.current !== initialStatusItems;
    const needsInit = workflowStatus.statusItems.length === 0 || isNewWorkflow;

    if (needsInit) {
      lastInitializedItemsRef.current = initialStatusItems;
      dispatch({
        type: 'initializeStatusItems',
        statusItems: initialStatusItems,
      });
    }
  }, [initialStatusItems, dispatch, workflowStatus.statusItems.length]);

  // Pusher subscription - only depends on channel to avoid re-subscribing
  // when initialStatusItems changes (which would cause missed events)
  useEffect(() => {
    // Only stop subscription when workflow is fully processed
    // Note: Don't stop on 'failed' because in continue_on_failure mode,
    // steps can still process after earlier steps fail
    if (!channel || workflowStatus.overallStatus === 'processed') {
      return;
    }

    const subscription = subscribe(channel);

    const createStepStatusHandler =
      (eventName: string) => (data: WorkflowStepData) => {
        if (data?.status) {
          dispatch({
            type: 'updateStepStatus',
            name: eventName,
            status: data.status,
            details: data.details,
          });
        }

        if (data.status === 'processed') {
          processedHandlers[eventName]?.(data);
        }
      };

    // Bind events
    subscription.bind(
      'crm-workflow-process-status-changed',
      (data: { overall_status?: string }) => {
        if (data?.overall_status) {
          dispatch({
            type: 'setOverallStatus',
            overallStatus: data.overall_status,
          });
        }
      }
    );
    initialStatusItems.forEach((item) => {
      subscription.bind(item.name, createStepStatusHandler(item.name));
    });

    return () => subscription.unsubscribe();
    // Note: intentionally NOT including initialStatusItems in deps to avoid
    // unsubscribe/resubscribe cycle that could miss events. Event names are
    // consistent for a workflow type, and channel changes when workflow changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, workflowStatus.overallStatus, dispatch, processedHandlers]);

  return {
    workflowStatus,
    dispatch,
    isProcessing: workflowStatus.overallStatus === 'processing',
    isComplete: workflowStatus.overallStatus === 'processed',
    isFailed: workflowStatus.overallStatus === 'failed',
  };
};

export const useNRIReviewData = () => {
  const nriReviewData = useAtomValue(nriReviewDataLoadableAtom);

  const rawData = nriReviewData.state === 'hasData' ? nriReviewData.data : null;

  const tractNRI = useMemo(
    () => (rawData ? mapSlotNRIResponseToTractNRI(rawData) : null),
    [rawData]
  );

  const slotNRI = useMemo(
    () => (rawData ? mapSlotNRIResponseToSlotNRI(rawData) : null),
    [rawData]
  );

  return {
    isLoading: nriReviewData.state === 'loading',
    tractNRI,
    slotNRI,
    error: nriReviewData.state === 'hasError' ? nriReviewData.error : null,
  };
};

// useWellboreExclusions
//
// Hook that exposes:
//   - committedExclusions: WellboreExclusion[] currently saved on the server
//   - committedUwis: Set<string> derived from committedExclusions for O(1) lookups
//   - stagedUwis: Set<string> for in-progress checkbox state in the modal
//   - resetStaged():    initialise staged set from current committed UWIs
//                       (call when opening the modal)
//   - toggleStaged(uwi): flip a UWI in the staged set
//   - save(reason?):    PUT the staged set; on success bumps the loadable's
//                       refresh counter so committedExclusions refetches.
//   - isLoading / error
export const useWellboreExclusions = () => {
  const loadable = useAtomValue(wellboreExclusionsLoadableAtom);
  const [stagedUwis, setStagedUwis] = useAtom(stagedExcludedUwisAtom);
  const bulkSet = useSetAtom(bulkSetWellboreExclusionsAtom);

  const committedExclusions = useMemo(
    () => (loadable.state === 'hasData' ? loadable.data : []),
    [loadable]
  );
  const committedUwis = useMemo(
    () => new Set(committedExclusions.map((e) => e.uwi)),
    [committedExclusions]
  );

  const resetStaged = useCallback(() => {
    setStagedUwis(new Set(committedUwis));
  }, [committedUwis, setStagedUwis]);

  const toggleStaged = useCallback(
    (uwi: string) => {
      setStagedUwis((prev) => {
        const next = new Set(prev);
        if (next.has(uwi)) next.delete(uwi);
        else next.add(uwi);
        return next;
      });
    },
    [setStagedUwis]
  );

  const save = useCallback(
    (reason?: string) =>
      bulkSet({ excludedUwis: Array.from(stagedUwis), reason }),
    [bulkSet, stagedUwis]
  );

  return {
    committedExclusions,
    committedUwis,
    stagedUwis,
    resetStaged,
    toggleStaged,
    save,
    isLoading: loadable.state === 'loading',
    error:
      loadable.state === 'hasError' ? (loadable.error as Error).message : null,
  };
};
